import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { BadgeCheck, Factory, Home, Scissors, Shuffle } from "lucide-react";
import { Card, Meta, Notice, Tag, DemoBadge, SampleBadge } from "./kit";
import { fmtDate } from "../lib/format";

export const BANNED = /identical|equivalen|interchangeab|guarantee|\bcures?\b|\bheals?\b|veterinar|идентичн|эквивалент|взаимозаменя|гарант|лечит|лечени|излеч|ветеринар/i;
export const uiLang = (i18n) => (i18n.language?.startsWith("ru") ? "ru" : "en");
export const localName = (p, lang) => (p?.lang_status?.[lang] === "human_reviewed" && p.editions?.[lang]?.name) || p?.name;
const SRC_PH = "Placeholder — no source supplied";
export const srcText = (t, s) => (s === SRC_PH ? t("cat.source_placeholder") : s);
export const tv = (t, ns, v) => t(`cat.${ns}.${v}`, { defaultValue: v });

export function LangNotice({ notice, shown, testid = "lang-notice" }) {
  const { t, i18n } = useTranslation();
  if (!notice) return null;
  const want = t(`lang.${uiLang(i18n)}`);
  return <Notice tone="warn" testid={testid}>{t(`cat.lang_${notice}`, { lang: want })}{shown ? ` ${t("cat.showing_in", { lang: t(`lang.${shown}`) })}` : ""}</Notice>;
}

export function ContextTag({ ctx, testid }) {
  const { t } = useTranslation();
  const Icon = ctx === "home" ? Home : Scissors;
  return <Tag data-testid={testid} className={ctx === "home" ? "bg-sage text-sage-ink" : "bg-peach text-clay-strong"}><Icon className="mr-1 inline h-3.5 w-3.5" aria-hidden />{t(`cat.ctx.${ctx}`)}</Tag>;
}

function Txt({ v }) {
  return <span className="whitespace-pre-wrap break-words">{v || "—"}</span>;
}

export function Alternatives({ items, linkBase }) {
  const { t } = useTranslation();
  if (!items?.length) return <p className="text-sm text-muted-foreground" data-testid="no-alternatives">{t("cat.no_alternatives")}</p>;
  return (
    <ul className="space-y-3" data-testid="alternatives-list">
      {items.map((a) => (
        <li key={a.id} className="rounded-2xl border bg-white p-4" data-testid={`alternative-${a.id}`}>
          <p className="font-bold"><Shuffle className="mr-1 inline h-4 w-4" aria-hidden />{t("cat.alt_to")}: {linkBase ? <Link className="inline-flex min-h-[44px] items-center underline" to={`${linkBase}/${a.to_id}`} data-testid={`alternative-link-${a.id}`}>{a.to_name}</Link> : a.to_name} <span className="font-normal text-muted-foreground">· {a.to_brand}</span></p>
          <LangNotice notice={a.lang_notice} shown={a.edition_lang} testid={`alt-lang-notice-${a.id}`} />
          <dl className="mt-2 grid gap-2 text-sm sm:grid-cols-2">
            {["reason", "shared_use", "differences", "limitations"].map((k) => <Meta key={k} label={t(`cat.alt.${k}`)}><Txt v={a.edition?.[k]} /></Meta>)}
            <Meta label={t("cat.alt.author")}>{a.author_name}</Meta><Meta label={t("cat.review_date")}>{fmtDate(a.review_date)}</Meta>
          </dl>
        </li>
      ))}
    </ul>
  );
}

export default function ProductCard({ p, linkBase }) {
  const { t } = useTranslation();
  const e = p.edition || {};
  const list = (ns, xs) => (xs?.length ? xs.map((x) => tv(t, ns, x)).join(", ") : "—");
  return (
    <div className="space-y-4" data-testid="product-card">
      <div className="flex flex-wrap items-center gap-2">{p.is_demo && <DemoBadge />}{p.sample && <SampleBadge testid={`sample-badge-${p.number}`} />}{p.use_context && <span data-testid="product-use-context">{p.use_context === "both" ? <><ContextTag ctx="salon" /> <ContextTag ctx="home" /></> : <ContextTag ctx={p.use_context} />}</span>}
        {p.submitted_by_name && <Tag data-testid="submitted-by">{t("cat.submitted_by", { name: p.submitted_by_name })}</Tag>}</div>
      <LangNotice notice={p.lang_notice} shown={p.edition_lang} />
      <Card className="grid gap-3 sm:grid-cols-3" data-testid="product-facts">
        <Meta label={t("cat.brand")} testid="product-brand">{p.brand || "—"}</Meta>
        <Meta label={t("cat.name")} testid="product-name">{p.name}</Meta>
        <Meta label={t("cat.f_category")} testid="product-category">{p.category ? tv(t, "category", p.category) : "—"}</Meta>
        <Meta label={t("cat.coat_types")}>{list("coat", p.coat_types)}</Meta>
        <Meta label={t("cat.purposes")}>{list("purpose", p.purposes)}</Meta>
        <Meta label={t("cat.grooming_tasks")}>{list("purpose", p.grooming_tasks)}</Meta>
        <Meta label={t("cat.source")} testid="product-source">{srcText(t, p.source) || "—"}</Meta>
        <Meta label={t("cat.review_date")} testid="product-review-date">{p.review_date ? fmtDate(p.review_date) : "—"}</Meta>
        <Meta label={t("cat.version")}>{p.version}</Meta>
      </Card>
      <section className="rounded-2xl border-2 border-lavender-mid bg-lavender p-5" data-testid="professional-guidance">
        <h2 className="flex items-center gap-2 text-base font-bold md:text-lg"><BadgeCheck className="h-5 w-5" aria-hidden />{t("cat.guidance_title")}</h2>
        <p className="mt-2 text-sm sm:text-base"><Txt v={e.guidance} /></p>
        <p className="mt-2 text-sm"><b>{t("cat.intended_purpose")}:</b> <Txt v={e.intended_purpose} /></p>
      </section>
      <section className="rounded-2xl border bg-white p-5" data-testid="manufacturer-information">
        <h2 className="flex items-center gap-2 text-base font-bold md:text-lg"><Factory className="h-5 w-5" aria-hidden />{t("cat.manufacturer_title")}</h2>
        <dl className="mt-2 grid gap-3 text-sm">
          <Meta label={t("cat.manufacturer_instructions")}><Txt v={e.manufacturer_instructions} /></Meta>
          <Meta label={t("cat.manufacturer_restrictions")}><Txt v={e.manufacturer_restrictions} /></Meta>
          <Meta label={t("cat.ingredients")} testid="product-ingredients">{p.ingredients?.trim() ? <Txt v={p.ingredients} /> : t("cat.not_provided")}</Meta>
        </dl>
      </section>
      <section data-testid="product-alternatives"><h2 className="mb-2 text-base font-bold md:text-lg">{t("cat.alternatives")}</h2><Alternatives items={p.alternatives} linkBase={linkBase} /></section>
    </div>
  );
}
